import {
  DownOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Empty, Popover, Spin, Tooltip } from "antd";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { ROUTES } from "../../common/constants";
import { AdAccountForm } from "./AdAccountForm";
import { adAccountStores } from "./AdAccountStores";

const AdAccount = observer(() => {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingIdx, setUpdatingIdx] = useState();

  const currentAcc = toJS(adAccountStores.currentAcc);
  const advertisers = toJS(adAccountStores.advertisers);
  const loading = toJS(adAccountStores.loading);

  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (!currentAcc?.id) {
      adAccountStores.getAdvertisers();
    }
  }, [currentAcc?.id]);

  const onCancelCreate = useCallback(() => {
    setCreating(false);
  }, []);

  const onCancelUpdating = useCallback(() => {
    setUpdatingIdx(undefined);
  }, []);

  const onSelectAcc = useCallback(
    (acc) => {
      adAccountStores.setCurrentAcc(acc);
      if ([ROUTES.adSet, ROUTES.ad].includes(location?.pathname)) {
        history.push(ROUTES.adCampaign);
      }
      setOpen(false);
    },
    [history, location?.pathname],
  );

  const content = useMemo(() => {
    return (
      <Spin spinning={loading}>
        <div className="w-[300px] sm:w-[400px] md:w-[500px] lg:w-[600px] max-h-[80vh]">
          <div className="overflow-y-auto overflow-x-hidden">
            {advertisers?.length ? (
              advertisers.map((acc, index) => (
                <div key={`${acc.account_name}_${index}`}>
                  {updatingIdx === index ? (
                    <div className="ml-2 py-[9px]">
                      <AdAccountForm acc={acc} onCancel={onCancelUpdating} />
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-4 hover:bg-[#f5f5f5] px-2 py-1.5 rounded-lg cursor-pointer"
                      onClick={() => onSelectAcc(acc)}
                    >
                      <UserOutlined className="text-base" />
                      <div className="flex-1 truncate">
                        <div className="font-semibold truncate">{acc.account_name}</div>
                        <div className="text-xs text-tgray truncate">{acc.description}</div>
                      </div>
                      <div className="flex gap-1.5">
                        <Tooltip title={t("Edit")}>
                          <EditOutlined
                            className="text-antd-primary p-1 rounded-sm hover:bg-[#e5e5e5]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUpdatingIdx(index);
                            }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <Empty className="!my-5" />
            )}
          </div>
          <div className="!mt-3 mb-1">
            {creating ? (
              <div className="mt-1 ml-2">
                <AdAccountForm onCancel={onCancelCreate} />
              </div>
            ) : (
              <Button type="dashed" className="w-full border-main text-main" onClick={() => setCreating(true)}>
                <div className="flex-center gap-2">
                  <PlusCircleOutlined />
                  <span>{t("Create")}</span>
                </div>
              </Button>
            )}
          </div>
        </div>
      </Spin>
    );
  }, [advertisers, creating, loading, onCancelCreate, onCancelUpdating, onSelectAcc, t, updatingIdx]);

  return (
    <Popover
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
      arrow={false}
      title={
        <span className="flex text-base gap-2 text-main">
          <UsergroupAddOutlined className="text-lg" /> {t("Advertiser account")}
        </span>
      }
      content={content}
      trigger="click"
    >
      <span className="flex items-center gap-2 text-white">
        {adAccountStores.loading ? <LoadingOutlined /> : currentAcc ? <UsergroupAddOutlined /> : <WarningOutlined />}
        <span className="text-ellipsis whitespace-nowrap word-break-word overflow-hidden">
          {currentAcc ? currentAcc.account_name : t("No data")}
        </span>
        <DownOutlined className="h6 text-white mt-2" />
      </span>
    </Popover>
  );
});

export default AdAccount;
